import React, { useState, useEffect } from 'react';
import { Box, Typography, Radio, Paper, Grid, TextField, Stack, MenuItem } from '@mui/material';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import AddIcon from '@mui/icons-material/Add';
import InfoIcon from '@mui/icons-material/Info';

const InvoiceOption = ({ id, title, subtitle, isSelected, onSelect }) => (
  <Paper
    elevation={0}
    onClick={() => onSelect(id)}
    sx={{ 
      p: 2.5, 
      borderRadius: '16px', 
      border: '1px solid',
      borderColor: isSelected ? '#7495f8' : '#F1F5F9',
      display: 'flex',
      alignItems: 'center',
      gap: 3,
      mb: 2,
      cursor: 'pointer',
      bgcolor: 'white',
      transition: 'all 0.2s',
      '&:hover': { borderColor: '#7495f8', bgcolor: '#F8FAFC' }
    }}
  >
    <Radio 
      checked={isSelected}
      sx={{ p: 0, color: '#E2E8F0', '&.Mui-checked': { color: '#7495f8' } }}
    />
    <Box sx={{ 
      width: 44, 
      height: 44, 
      borderRadius: '12px', 
      bgcolor: '#EFF6FF', 
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center',
      color: '#0E2465'
    }}>
      {id === 'new' ? <AddIcon /> : <ReceiptLongIcon fontSize="small" />}
    </Box>
    <Box sx={{ flex: 1 }}>
      <Typography variant="subtitle2" fontWeight="900" color="#0E2465">{title}</Typography>
      <Typography variant="caption" color="#737791" sx={{ fontWeight: 500, display: 'block', mt: 0.5 }}>{subtitle}</Typography>
    </Box>
  </Paper>
);

const BookingInvoice = ({ 
  facturacion, 
  selectedInvoiceId, setSelectedInvoiceId,
  newInvoice, setNewInvoice 
}) => {
  const [showNewForm, setShowNewForm] = useState(selectedInvoiceId === 'new');

  useEffect(() => {
    if (facturacion.length === 0 && selectedInvoiceId !== 'new') {
      setSelectedInvoiceId('new');
      setShowNewForm(true);
    }
  }, [facturacion.length, selectedInvoiceId, setSelectedInvoiceId]);

  const handleSelect = (id) => {
    setSelectedInvoiceId(id);
    setShowNewForm(id === 'new');
  };

  const handleChange = (e) => {
    setNewInvoice({
      ...newInvoice,
      [e.target.name]: e.target.value
    });
  };

  const regimenes = [
    "601 - General de Ley Personas Morales",
    "603 - Personas Morales con Fines no Lucrativos",
    "605 - Sueldos y Salarios e Ingresos Asimilados a Salarios",
    "606 - Arrendamiento",
    "612 - Personas Físicas con Actividades Empresariales y Profesionales",
    "621 - Incorporación Fiscal",
    "626 - Régimen Simplificado de Confianza"
  ];

  const usosCFDI = [
    "G01 - Adquisición de mercancías",
    "G03 - Gastos en general",
    "P01 - Por definir",
    "S01 - Sin efectos fiscales"
  ];

  return (
    <Box>
      {facturacion.length > 0 && (
        <>
          <Typography variant="caption" color="#737791" fontWeight="900" sx={{ display: 'block', mb: 2, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            TUS DATOS FISCALES
          </Typography>
          {facturacion.map((item) => (
            <InvoiceOption 
              key={item._id}
              id={item._id}
              title={item.nombre}
              subtitle={`${item.rfc} - ${item.regimenFiscal}`}
              isSelected={selectedInvoiceId === item._id}
              onSelect={handleSelect}
            />
          ))}
        </>
      )}

      <Typography variant="caption" color="#737791" fontWeight="900" sx={{ display: 'block', mt: 3, mb: 2, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
        AGREGAR NUEVOS DATOS FISCALES
      </Typography>
      <InvoiceOption 
        id="new"
        title="Nuevos datos fiscales"
        subtitle="RFC y dirección de facturación"
        isSelected={selectedInvoiceId === 'new'}
        onSelect={handleSelect}
      />

      {showNewForm && (
        <Box sx={{ mt: 4, p: 3, borderRadius: '20px', bgcolor: '#F8FAFC', border: '1px solid #E2E8F0' }}>
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 3 }}>
            <ReceiptLongIcon sx={{ color: '#0E2465', fontSize: 20 }} />
            <Typography variant="caption" color="#64748B" fontWeight={500}>
              Ingresa tus datos fiscales para recibir tu factura
            </Typography>
          </Stack>

          <Grid container spacing={2}>
            <Grid item size={12}>
              <Typography variant="caption" fontWeight="bold" color="#0E2465" sx={{ mb: 1, display: 'block' }}>RFC *</Typography>
              <TextField 
                fullWidth name="rfc" placeholder="XAXX010101000" 
                value={newInvoice.rfc} onChange={handleChange}
                variant="outlined" size="small"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px', bgcolor: 'white' } }}
              />
            </Grid>

            <Grid item size={12}>
              <Typography variant="caption" fontWeight="bold" color="#0E2465" sx={{ mb: 1, display: 'block' }}>Razón Social *</Typography>
              <TextField 
                fullWidth name="razonSocial" placeholder="NOMBRE COMPLETO O RAZÓN SOCIAL" 
                value={newInvoice.razonSocial} onChange={handleChange}
                variant="outlined" size="small"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px', bgcolor: 'white' } }}
              />
            </Grid>

            <Grid item size={12}>
              <Typography variant="caption" fontWeight="bold" color="#0E2465" sx={{ mb: 1, display: 'block' }}>Dirección Fiscal *</Typography>
              <TextField 
                fullWidth name="direccionFiscal" placeholder="Calle, Número, Colonia" 
                value={newInvoice.direccionFiscal} onChange={handleChange}
                variant="outlined" size="small"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px', bgcolor: 'white' } }}
              />
            </Grid>

            <Grid item size={12}>
              <Typography variant="caption" fontWeight="bold" color="#0E2465" sx={{ mb: 1, display: 'block' }}>Código Postal *</Typography>
              <TextField 
                fullWidth name="cp" placeholder="01000" 
                value={newInvoice.cp} onChange={handleChange}
                variant="outlined" size="small"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px', bgcolor: 'white' } }}
              />
            </Grid>

            <Grid item size={12}>
              <Typography variant="caption" fontWeight="bold" color="#0E2465" sx={{ mb: 1, display: 'block' }}>Régimen Fiscal *</Typography>
              <TextField 
                select fullWidth name="regimenFiscal" 
                value={newInvoice.regimenFiscal} onChange={handleChange}
                variant="outlined" size="small"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px', bgcolor: 'white' } }}
              >
                {regimenes.map((reg) => (
                  <MenuItem key={reg} value={reg}>{reg}</MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item size={12}>
              <Typography variant="caption" fontWeight="bold" color="#0E2465" sx={{ mb: 1, display: 'block' }}>Uso de CFDI *</Typography>
              <TextField 
                select fullWidth name="usoCFDI" 
                value={newInvoice.usoCFDI} onChange={handleChange}
                variant="outlined" size="small"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px', bgcolor: 'white' } }}
              >
                {usosCFDI.map((uso) => (
                  <MenuItem key={uso} value={uso}>{uso}</MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>

          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mt: 4, p: 2, borderRadius: '12px', bgcolor: 'white', border: '1px solid #E2E8F0' }}>
            <InfoIcon sx={{ color: '#FCD34D', fontSize: 20 }} />
            <Typography variant="caption" color="#64748B" fontWeight={500}>
              Asegúrate de que tus datos fiscales sean correctos. La factura será enviada a tu correo electrónico.
            </Typography>
          </Stack>
        </Box>
      )}
    </Box>
  );
};

export default BookingInvoice;
